import {
  getMemberImportPreview,
  MemberImportPreviewApiError,
  type MemberImportPreviewKind,
  type MemberImportPreviewResponse,
  type MemberImportPreviewRow,
} from '@urbanik/api-client';
import { DataTable, StatePanel, canonicalStates, type DataColumn } from '@urbanik/ui';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
import './member-import-diagnostic.css';
type State = 'loading' | 'success' | 'empty' | 'error' | 'forbidden';
const stateDef = (kind: State) => canonicalStates.find((s) => s.kind === kind)!;
const labels: Record<MemberImportPreviewKind, string> = {
  valid: 'Válida',
  warning: 'Advertencia',
  error: 'Error',
};
const columns: readonly DataColumn<MemberImportPreviewRow>[] = [
  {
    id: 'slotNumber',
    header: 'N.º de cupo',
    accessor: (r) => r.normalized.slotNumber ?? r.original.slotNumber ?? '—',
    cell: (r) => <strong>{r.normalized.slotNumber ?? r.original.slotNumber ?? '—'}</strong>,
  },
  { id: 'row', header: 'Fila Excel', accessor: (r) => String(r.rowNumber) },
  {
    id: 'kind',
    header: 'Resultado',
    accessor: (r) => labels[r.kind],
    cell: (r) => <strong>{labels[r.kind]}</strong>,
  },
  { id: 'member', header: 'Socio original', accessor: (r) => r.original.fullName ?? '—' },
  {
    id: 'normalized',
    header: 'Estado normalizado',
    accessor: (r) => r.normalized.status ?? 'null',
    cell: (r) => <code>{r.normalized.status ?? 'null'}</code>,
  },
  {
    id: 'issues',
    header: 'Detalle',
    accessor: (r) => (r.issues.length ? r.issues.join(' · ') : 'Sin incidencias'),
  },
];
export function MemberImportPreviewView() {
  const [attempt, setAttempt] = useState(0),
    [state, setState] = useState<State>('loading'),
    [data, setData] = useState<MemberImportPreviewResponse | null>(null),
    [filter, setFilter] = useState<'all' | MemberImportPreviewKind>('all');
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getMemberImportPreview(apiBase, controller.signal)
      .then((result) => {
        setData(result);
        setState(result.rows.length ? 'success' : 'empty');
      })
      .catch((error: unknown) =>
        setState(
          error instanceof MemberImportPreviewApiError && error.kind === 'forbidden'
            ? 'forbidden'
            : 'error',
        ),
      );
    return () => controller.abort();
  }, [attempt]);
  const rows = useMemo(
    () => data?.rows.filter((row) => filter === 'all' || row.kind === filter) ?? [],
    [data, filter],
  );
  if (state !== 'success' || !data)
    return state === 'error' ? (
      <StatePanel state={stateDef(state)} onAction={() => setAttempt((v) => v + 1)} />
    ) : (
      <StatePanel state={stateDef(state)} />
    );
  return (
    <div className="shell-page import-diagnostic-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M04.3 · Vista previa</p>
          <h1>Revisa cada fila antes de simular.</h1>
          <p>Valores originales, normalizados e incidencias. Esta vista no escribe datos.</p>
        </div>
        <span className="surface-badge">Solo lectura</span>
      </header>
      <section className="diagnostic-summary" aria-label="Resumen de clasificación">
        <article>
          <span>Válidas</span>
          <strong>{data.valid}</strong>
        </article>
        <article>
          <span>Advertencias</span>
          <strong>{data.warning}</strong>
        </article>
        <article>
          <span>Errores</span>
          <strong>{data.error}</strong>
        </article>
      </section>
      <nav aria-label="Filtros de vista previa" className="preview-filters">
        {(
          [
            ['all', 'Todas', data.total],
            ['valid', 'Válidas', data.valid],
            ['warning', 'Advertencias', data.warning],
            ['error', 'Errores', data.error],
          ] as const
        ).map(([id, label, count]) => (
          <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}>
            {label} ({count})
          </button>
        ))}
      </nav>
      <section aria-labelledby="preview-title">
        <div className="section-title">
          <p className="kicker">{data.sourceSheet}</p>
          <h2 id="preview-title">Detalle por fila Excel</h2>
        </div>
        <DataTable
          columns={columns}
          rows={rows}
          visibleColumnIds={new Set(columns.map((c) => c.id))}
          sort={{ columnId: 'row', direction: 'ascending' }}
          onSort={() => {}}
          empty={<StatePanel state={stateDef('empty')} />}
        />
      </section>
    </div>
  );
}
